import { useCallback } from 'react';

import { commitLocalUpdate, ConnectionHandler, useRelayEnvironment } from 'react-relay';

import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import RelayDataID from '@atlassian/relay-data-id';

/**
 * Hook that provides a function to update the `onlyVerifiedAgents` Relay connection
 * by adding or removing agent edges based on the verified state.
 *
 * This only handles local cache updates — the actual mutation is performed
 * by AgentVerificationDropdownItem.
 */
export const useAgentVerification: (
	props: Omit<AssistanceServiceInjectedParams, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	updateVerifiedAgentsConnection: (agentAri: string, verified: boolean) => void;
} = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams }: AssistanceServiceInjectedParams) => {
		const environment = useRelayEnvironment();

		const updateVerifiedAgentsConnection = useCallback(
			(agentAri: string, verified: boolean) => {
				commitLocalUpdate(environment, (store) => {
					const id = RelayDataID(
						{
							id: agentAri,
						},
						'AgentStudioAssistant',
					);

					if (!id) {
						return;
					}

					const agentRecord = store.get(id);
					if (agentRecord) {
						agentRecord.setValue(verified, 'isVerified');
					}

					const root = store.getRoot();

					const connection = ConnectionHandler.getConnection(
						root,
						'paginatedAgentsList_agentStudio_getAgents',
						{
							cloudId: assistanceServiceInjectedParams.siteId,
							input: {
								onlyVerifiedAgents: true,
							},
							product: assistanceServiceInjectedParams.product,
						},
					);

					const agentDataID = agentRecord?.getDataID();

					if (!connection || !agentDataID || !agentRecord) {
						return;
					}

					if (verified) {
						const edge = ConnectionHandler.createEdge(
							store,
							connection,
							agentRecord,
							'AgentStudioAgentEdge',
						);

						ConnectionHandler.insertEdgeAfter(connection, edge);
					} else {
						ConnectionHandler.deleteNode(connection, agentDataID);
					}
				});
			},
			[
				assistanceServiceInjectedParams.product,
				assistanceServiceInjectedParams.siteId,
				environment,
			],
		);

		return {
			updateVerifiedAgentsConnection,
		};
	},
);
